import React, { useCallback, useEffect, useState } from 'react';
import {
  AppInput,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import {
  getControlClass,
  getControlDocumentation,
  getControlEfficiency,
  getControlState,
  getControlType,
  getFrequency,
} from '../../../tools/totalEnergiesRiskControlTools';
import TotalEnergiesRiskRiskSelect from '../../../../../../components/form/Selects/TotalEnergiesRiskRiskSelect';
import ResponsibleSelect from '../../../../../../components/form/Selects/ResponsibleSelect';

export default function ControlFields({
  values = {},
  errors = {},
  setFieldValue = () => {},
  isReadOnly = false,
}) {
  const [controlTypeOptions] = useState(
    Array.from({ length: 3 }, (_, i) => ({
      value: i,
      label: getControlType(i),
    }))
  );
  const [controlClassOptions] = useState(
    Array.from({ length: 3 }, (_, i) => ({
      value: i,
      label: getControlClass(i),
    }))
  );
  const [frequencyOptions] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      value: i,
      label: getFrequency(i),
    }))
  );
  const [controlDocumentationOptions] = useState(
    Array.from({ length: 3 }, (_, i) => ({
      value: i,
      label: getControlDocumentation(i),
    }))
  );
  const [controlEfficiencyOptions] = useState(
    Array.from({ length: 4 }, (_, i) => ({
      value: i,
      label: getControlEfficiency(i),
    }))
  );
  const [stateOptions] = useState(
    Array.from({ length: 4 }, (_, i) => ({
      value: i,
      label: getControlState(i),
    }))
  );

  const checkRiskID = useCallback(() => {
    if (values?.secondaryRiskIDs?.includes(parseInt(values?.riskID))) {
      setFieldValue('riskID', null);
    }
  }, [values]);

  const checkSecondaryRiskIDs = useCallback(() => {
    if (values?.secondaryRiskIDs) {
      setFieldValue(
        'secondaryRiskIDs',
        values?.secondaryRiskIDs?.filter((sri) => sri !== parseInt(values?.riskID))
      );
    }
  }, [values]);

  useEffect(() => {
    checkSecondaryRiskIDs();
  }, [values?.riskID]);

  useEffect(() => {
    checkRiskID();
  }, [values?.secondaryRiskIDs]);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-lg-6">
          <AppTextArea
            title="Nombre"
            value={values.name}
            onChange={(value) => setFieldValue('name', value)}
            error={errors.name}
            resize={!isReadOnly}
            required={!isReadOnly}
            isReadOnly={isReadOnly}
          />
        </div>
        <div className="col-lg-6">
          <AppTextArea
            title="Descripción"
            value={values.description}
            onChange={(value) => setFieldValue('description', value)}
            error={errors.description}
            resize={!isReadOnly}
            isReadOnly={isReadOnly}
          />
        </div>
        <div className="col-lg-4">
          {isReadOnly ? (
            <AppInput
              title="Estado de control"
              value={getControlState(values?.state || 0)}
              isReadOnly={isReadOnly}
            />
          ) : (
            <AppSingleSelect
              options={stateOptions}
              title="Estado de control"
              value={values?.state}
              error={errors?.state}
              onChange={(opt) => {
                setFieldValue('state', opt);
              }}
              required
            />
          )}
        </div>
        <div className="col-lg-4">
          {isReadOnly ? (
            <AppInput
              title="Riesgo principal"
              value={values?.riskCode}
              isReadOnly={isReadOnly}
            />
          ) : (
            <TotalEnergiesRiskRiskSelect
              value={parseInt(values?.riskID)}
              error={errors?.riskID}
              onHandleChange={(opt) => setFieldValue('riskID', opt)}
              hasSearchBox
              isClearable
              required
            />
          )}
        </div>
        <div
          className="col-lg-4"
          title={
            isReadOnly && values?.secondaryRiskCodes?.length > 0
              ? values?.secondaryRiskCodes?.join('\n')
              : ''
          }
        >
          {isReadOnly ? (
            <AppInput
              title="Riesgos secundarios"
              value={
                values?.secondaryRiskCodes?.length > 0
                  ? values?.secondaryRiskCodes.join('\n')
                  : ''
              }
              isReadOnly={isReadOnly}
            />
          ) : (
            <TotalEnergiesRiskRiskSelect
              title="Riesgos secundarios"
              value={values?.secondaryRiskIDs}
              error={errors?.secondaryRiskIDs}
              onHandleChange={(opt) => setFieldValue('secondaryRiskIDs', opt)}
              hasSearchBox
              hasSelectOptions
              isClearable
              isMulti
            />
          )}
        </div>
        <div className="col-lg-4">
          {isReadOnly ? (
            <AppInput
              title="Responsables"
              value={values?.responsibleName || ''}
              isReadOnly={isReadOnly}
            />
          ) : (
            <ResponsibleSelect
              value={values.responsibleID}
              error={errors?.responsibleID}
              onHandleChange={(opt) => setFieldValue('responsibleID', opt)}
              apiCall="api_r_user_module_totalEnergiesRisk_control_getResponsibles"
              hasSearchBox
              isClearable
              isMulti={false}
              required
            />
          )}
        </div>
        <div className="col-lg-4">
          {isReadOnly ? (
            <AppInput
              title="Tipo de Control"
              value={getControlType(values?.controlType)}
              isReadOnly={isReadOnly}
            />
          ) : (
            <AppSingleSelect
              options={controlTypeOptions}
              title="Tipo de Control"
              value={values?.controlType}
              error={errors?.controlType}
              onChange={(opt) => {
                setFieldValue('controlType', opt);
              }}
              hasSearchBox
            />
          )}
        </div>
        <div className="col-lg-4">
          {isReadOnly ? (
            <AppInput
              title="Clase de Control"
              value={getControlClass(values?.controlClass)}
              isReadOnly={isReadOnly}
            />
          ) : (
            <AppSingleSelect
              options={controlClassOptions}
              title="Clase de Control"
              value={values?.controlClass}
              error={errors?.controlClass}
              onChange={(opt) => {
                setFieldValue('controlClass', opt);
              }}
              hasSearchBox
            />
          )}
        </div>
        <div className="col-lg-4">
          {isReadOnly ? (
            <AppInput
              title="Frecuencia"
              value={getFrequency(values?.frequency)}
              isReadOnly={isReadOnly}
            />
          ) : (
            <AppSingleSelect
              options={frequencyOptions}
              title="Frecuencia"
              value={values?.frequency}
              error={errors?.frequency}
              onChange={(opt) => {
                setFieldValue('frequency', opt);
              }}
              hasSearchBox
            />
          )}
        </div>
        <div className="col-lg-4">
          {isReadOnly ? (
            <AppInput
              title="Documentación del control"
              value={getControlDocumentation(values?.controlDocumentation)}
              isReadOnly={isReadOnly}
            />
          ) : (
            <AppSingleSelect
              options={controlDocumentationOptions}
              title="Documentación del control"
              value={values?.controlDocumentation}
              error={errors?.controlDocumentation}
              onChange={(opt) => {
                setFieldValue('controlDocumentation', opt);
              }}
              hasSearchBox
            />
          )}
        </div>
        <div className="col-lg-4">
          {isReadOnly ? (
            <AppInput
              title="Efectividad del control"
              value={getControlEfficiency(values?.controlEfficiency)}
              isReadOnly={isReadOnly}
            />
          ) : (
            <AppSingleSelect
              options={controlEfficiencyOptions}
              title="Efectividad del control"
              value={values?.controlEfficiency}
              error={errors?.controlEfficiency}
              onChange={(opt) => {
                setFieldValue('controlEfficiency', opt);
              }}
              hasSearchBox
            />
          )}
        </div>
      </div>
    </div>
  );
}
